import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Download,
  Trophy,
  TrendingUp,
  Star,
  Award,
  Users,
  DollarSign,
  Target,
  Sparkles,
  Share2,
  MapPin,
  Calendar,
  Building2,
  PartyPopper,
  Rocket,
  Heart
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Real data from Executive Summary
const YEARLY_DATA = {
  period: "December 30, 2024 - December 28, 2025",
  totalNetSales: 12578740.29,
  previousYearSales: 10024063.67,
  salesGrowth: 25.49,
  totalCustomers: 851382,
  customerGrowth: 21.58,
  avgCheck: 14.77,
  avgCheckGrowth: 3.14,
  driveThruPercent: 41.53,
  foodCostPercent: 28.34,
  laborPercent: 21.34,
  splh: 5.61,
  stores: [
    { name: "McAllister Place", code: "7216", sales: 843734.59, growth: 12.43, customers: 64932 },
    { name: "Rothesay (Hampton Rd)", code: "7006", sales: 1251576.48, growth: 4.76, customers: 84677 },
    { name: "Regent Mall", code: "7120", sales: 931039.03, growth: 11.28, customers: 70466 },
    { name: "Elmwood (Moncton)", code: "7226", sales: 2563356.15, growth: 6.60, customers: 162399 },
    { name: "Miramichi", code: "7237", sales: 2101394.77, growth: 39.99, customers: 124624 },
    { name: "Clarenville", code: "9232", sales: 2073491.02, growth: 9.41, customers: 146541 },
    { name: "Deer Lake", code: "9240", sales: 870595.33, growth: 7.80, customers: 60882 },
    { name: "White Rose Drive", code: "9003", sales: 201912.11, growth: 0, customers: 9290, isNew: true },
    { name: "Bathurst", code: "7511", sales: 0, growth: 0, customers: 0, isNew: true, grandOpening: true }
  ]
};

// Store expansion timeline
const EXPANSION_TIMELINE = [
  { month: "April 2025", store: "Clarenville (Shoal Harbour)", icon: "🏪", color: "bg-green-500" },
  { month: "July 2025", store: "Deer Lake", icon: "🦌", color: "bg-blue-500" },
  { month: "October 2025", store: "Quispamsis", icon: "🍂", color: "bg-orange-500" },
  { month: "December 2025", store: "Bathurst", icon: "🎄", color: "bg-red-500", grandOpening: true },
  { month: "December 2025", store: "White Rose", icon: "🌹", color: "bg-pink-500" }
];

export default function YearlySummary() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const newsletterRef = useRef(null);

  const handleDownload = async () => {
    if (!newsletterRef.current) return;
    
    try {
      setLoading(true);
      // Dynamic import for html2canvas
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(newsletterRef.current, { 
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true
      });
      const link = document.createElement('a');
      link.download = `Elmwood_2025_Year_In_Review.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Newsletter downloaded! Ready to share on WhatsApp 📱');
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const text = `🎉 ELMWOOD MANAGEMENT - 2025 YEAR IN REVIEW 🎉\n\n` +
      `📈 INCREDIBLE GROWTH!\n` +
      `• Grew from 4 stores to 9 stores!\n` +
      `• Total Sales: $${(YEARLY_DATA.totalNetSales/1000000).toFixed(2)}M (+${YEARLY_DATA.salesGrowth}%)\n` +
      `• Served ${YEARLY_DATA.totalCustomers.toLocaleString()} guests (+${YEARLY_DATA.customerGrowth}%)\n\n` +
      `🏪 NEW LOCATIONS:\n` +
      `• April - Clarenville\n` +
      `• July - Deer Lake\n` +
      `• October - Quispamsis\n` +
      `• December - Bathurst (GRAND OPENING!)\n` +
      `• December - White Rose\n\n` +
      `🏆 TOP PERFORMERS:\n` +
      `• Douglastown: +40% growth!\n` +
      `• McAllister Place: +12.4% growth\n` +
      `• Regent Mall: +11.3% growth\n\n` +
      `Thank you to our amazing team! 🙏\n` +
      `#AWCanada #ElmwoodManagement #2025Review`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Elmwood 2025 Year in Review', text });
      } catch (err) {
        navigator.clipboard.writeText(text);
        toast.success('Summary copied to clipboard!');
      }
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Summary copied to clipboard! Paste in WhatsApp 📱');
    }
  };

  if (!['owner', 'area_manager'].includes(user?.role)) {
    return (
      <div className="text-center py-12">
        <Award className="w-12 h-12 mx-auto mb-4 text-slate-300" />
        <h2 className="text-lg font-bold text-slate-900 mb-2">Access Restricted</h2>
        <p className="text-slate-600">Only owners can view yearly summaries</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="yearly-summary">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <Trophy className="w-8 h-8" />
              2025 Year in Review
            </h1>
            <p className="text-white/80 text-sm mt-1">From 4 stores to 9 stores - An incredible year of growth!</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleShare} variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-sm">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button onClick={handleDownload} disabled={loading} className="bg-white text-orange-600 hover:bg-orange-50 rounded-sm font-semibold">
              <Download className="w-4 h-4 mr-2" />
              {loading ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </div>

      {/* Newsletter Content */}
      <div ref={newsletterRef} className="bg-white rounded-lg p-8 space-y-8 shadow-lg">
        {/* Header Banner */}
        <div className="text-center pb-6 border-b-4 border-orange-500">
          <div className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png"
              alt="A&W Logo"
              className="w-24 h-24 object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            🎉 2025 Year in Review 🎉
          </h1>
          <p className="text-2xl text-orange-600 font-bold">Elmwood Management | A&W Canada</p>
          <p className="text-slate-500 mt-2 text-lg">An Incredible Year of Growth & Excellence</p>
        </div>

        {/* Big Announcement */}
        <div className="bg-gradient-to-r from-orange-100 to-amber-100 rounded-xl p-6 text-center">
          <PartyPopper className="w-12 h-12 mx-auto text-orange-600 mb-3" />
          <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            We Grew From 4 to 9 Stores! 🚀
          </h2>
          <p className="text-lg text-slate-600">More than doubled our footprint across Atlantic Canada</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-green-50 rounded-xl p-6 text-center border-2 border-green-200">
            <DollarSign className="w-10 h-10 mx-auto text-green-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900">${(YEARLY_DATA.totalNetSales/1000000).toFixed(2)}M</p>
            <p className="text-sm text-slate-500">Total Sales</p>
            <p className="text-green-600 font-semibold mt-1">+{YEARLY_DATA.salesGrowth}% vs LY</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-6 text-center border-2 border-blue-200">
            <Users className="w-10 h-10 mx-auto text-blue-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900">{(YEARLY_DATA.totalCustomers/1000).toFixed(0)}K</p>
            <p className="text-sm text-slate-500">Guests Served</p>
            <p className="text-blue-600 font-semibold mt-1">+{YEARLY_DATA.customerGrowth}% vs LY</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-6 text-center border-2 border-purple-200">
            <Building2 className="w-10 h-10 mx-auto text-purple-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900">9</p>
            <p className="text-sm text-slate-500">Store Locations</p>
            <p className="text-purple-600 font-semibold mt-1">+5 new stores!</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-6 text-center border-2 border-amber-200">
            <TrendingUp className="w-10 h-10 mx-auto text-amber-600 mb-2" />
            <p className="text-3xl font-bold text-slate-900">${YEARLY_DATA.avgCheck}</p>
            <p className="text-sm text-slate-500">Avg Check</p>
            <p className="text-amber-600 font-semibold mt-1">+{YEARLY_DATA.avgCheckGrowth}% vs LY</p>
          </div>
        </div>

        {/* Expansion Timeline */}
        <div className="py-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Rocket className="w-6 h-6 text-orange-600" />
            🗓️ 2025 Expansion Journey
          </h2>
          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-orange-200"></div>
            <div className="space-y-6">
              {EXPANSION_TIMELINE.map((item, index) => (
                <div key={index} className="flex items-center gap-4 relative">
                  <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center text-2xl z-10 shadow-lg`}>
                    {item.icon}
                  </div>
                  <div className={`flex-1 p-4 rounded-lg ${item.grandOpening ? 'bg-gradient-to-r from-red-100 to-orange-100 border-2 border-red-300' : 'bg-slate-50'}`}>
                    <p className="font-bold text-slate-900 text-lg">{item.store}</p>
                    <p className="text-slate-500">{item.month}</p>
                    {item.grandOpening && (
                      <span className="inline-block mt-1 px-3 py-1 bg-red-500 text-white text-xs rounded-full font-bold">
                        🎊 GRAND OPENING!
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="py-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Trophy className="w-6 h-6 text-yellow-500" />
            🏆 Top Performing Stores
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Gold - Douglastown */}
            <div className="bg-gradient-to-br from-yellow-100 to-amber-100 rounded-xl p-6 border-2 border-yellow-400 text-center">
              <div className="text-4xl mb-2">🥇</div>
              <h3 className="text-xl font-bold text-slate-900">Douglastown</h3>
              <p className="text-3xl font-bold text-yellow-600 my-2">+40%</p>
              <p className="text-slate-600">Sales Growth</p>
              <p className="text-sm text-slate-500 mt-2">$2.1M in sales | 125K guests</p>
            </div>
            {/* Silver - McAllister */}
            <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6 border-2 border-slate-300 text-center">
              <div className="text-4xl mb-2">🥈</div>
              <h3 className="text-xl font-bold text-slate-900">McAllister Place</h3>
              <p className="text-3xl font-bold text-slate-600 my-2">+12.4%</p>
              <p className="text-slate-600">Sales Growth</p>
              <p className="text-sm text-slate-500 mt-2">$844K in sales | 65K guests</p>
            </div>
            {/* Bronze - Regent */}
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl p-6 border-2 border-orange-300 text-center">
              <div className="text-4xl mb-2">🥉</div>
              <h3 className="text-xl font-bold text-slate-900">Regent Mall</h3>
              <p className="text-3xl font-bold text-orange-600 my-2">+11.3%</p>
              <p className="text-slate-600">Sales Growth</p>
              <p className="text-sm text-slate-500 mt-2">$931K in sales | 70K guests</p>
            </div>
          </div>
        </div>

        {/* All Stores Performance */}
        <div className="py-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Star className="w-6 h-6 text-orange-500" />
            ⭐ Store Performance Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-orange-50">
                  <th className="text-left py-3 px-4 font-bold text-slate-700 rounded-tl-lg">Store</th>
                  <th className="text-right py-3 px-4 font-bold text-slate-700">Net Sales</th>
                  <th className="text-right py-3 px-4 font-bold text-slate-700">Growth</th>
                  <th className="text-right py-3 px-4 font-bold text-slate-700 rounded-tr-lg">Guests</th>
                </tr>
              </thead>
              <tbody>
                {YEARLY_DATA.stores.filter(s => s.sales > 0).sort((a, b) => b.sales - a.sales).map((store, index) => (
                  <tr key={index} className={`border-b border-slate-100 ${store.isNew ? 'bg-green-50' : ''}`}>
                    <td className="py-3 px-4 font-medium">
                      {store.name}
                      {store.isNew && <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs rounded-full">NEW</span>}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">${(store.sales/1000).toFixed(0)}K</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-bold ${store.growth >= 10 ? 'text-green-600' : store.growth >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {store.growth > 0 ? '+' : ''}{store.growth}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">{store.customers.toLocaleString()}</td>
                  </tr>
                ))}
                <tr className="bg-orange-100 font-bold">
                  <td className="py-3 px-4 rounded-bl-lg">TOTAL</td>
                  <td className="py-3 px-4 text-right">${(YEARLY_DATA.totalNetSales/1000000).toFixed(2)}M</td>
                  <td className="py-3 px-4 text-right text-green-600">+{YEARLY_DATA.salesGrowth}%</td>
                  <td className="py-3 px-4 text-right rounded-br-lg">{YEARLY_DATA.totalCustomers.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Thank You Message */}
        <div className="text-center py-8 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
          <Heart className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            🙏 Thank You to Our Amazing Team!
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
            2025 was a year of tremendous growth and achievement. From opening 5 new locations to 
            serving over 850,000 guests, your dedication and hard work made it all possible.
          </p>
          <p className="text-xl text-slate-700 font-semibold">
            Here's to an even more incredible 2026! 🚀
          </p>
          <p className="mt-6 text-orange-600 font-bold text-lg">- Mohit Chugh, Owner</p>
        </div>

        {/* Footer */}
        <div className="text-center pt-6 border-t border-slate-200">
          <p className="text-sm text-slate-500">
            Elmwood Management | A&W Canada | 9 Locations Across Atlantic Canada
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Data Period: {YEARLY_DATA.period}
          </p>
        </div>
      </div>
    </div>
  );
}
